import { useState, useCallback, useEffect } from "react";
import axios from "axios";

// Import our new components
import TemplateHeader from "./TemplateHeader";
import TemplateBody from "./TemplateBody";
import TemplateFooter from "./TemplateFooter";
import TemplateButtons from "./TemplateButtons";
import TemplatePreview from "./TemplatePreview";
import ValidationUtils from "./utils/ValidationUtils";

import "./css/TemplateForm.css";

function TemplateForm() {
  const [formData, setFormData] = useState({
    name: "",
    language: "en",
    category: "UTILITY",
    header: {
      type: "NONE",
      text: "",
      example: "",
      url: "",
    },
    bodyText: "",
    footer: "",
    whatsappTemplateId: "",
    buttons: [],
  });

  const [file, setFile] = useState(null);
  const [variableSamples, setVariableSamples] = useState([]);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Update form data fields
  const updateFormData = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Update header fields
  const updateHeader = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      header: {
        ...prev.header,
        [field]: value,
      },
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    // Validate form
    const validationError = ValidationUtils.validateForm(
      formData,
      variableSamples,
      file
    );
    if (validationError) {
      setError(validationError);
      setIsSubmitting(false);
      return;
    }

    try {
      const uploadFormData = new FormData();
      let fileHandle = null;

      // Upload media file if exists
      if (
        file &&
        ["IMAGE", "VIDEO", "DOCUMENT"].includes(formData.header.type)
      ) {
        uploadFormData.append("file", file);

        // First upload to our server
        const uploadResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/templates/upload`,
          uploadFormData
        );

        // Then get the file handle from Meta
        const metaUploadResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/templates/upload-to-meta`,
          {
            filePath: uploadResponse.data.path,
            format: formData.header.type,
          },
          {
            headers: {
              "Content-Type": "application/json",
              "x-email": localStorage.getItem("tenantEmail"),
            },
          }
        );

        fileHandle = metaUploadResponse.data.fileHandle;
      }

      // Build components
      const components = [];

      if (formData.header.type !== "NONE") {
        const headerObj = {
          type: "HEADER",
          format: formData.header.type,
        };

        if (formData.header.type === "TEXT") {
          headerObj.text = formData.header.text;
        } else if (fileHandle) {
          headerObj.example = { header_handle: [fileHandle] };
        }

        components.push(headerObj);
      }

      const bodyObj = {
        type: "BODY",
        text: formData.bodyText,
      };

      // Add variable samples if they exist
      if (variableSamples.length > 0) {
        bodyObj.example = {
          body_text: [variableSamples],
        };
      }
      components.push(bodyObj);

      if (formData.footer) {
        components.push({
          type: "FOOTER",
          text: formData.footer,
        });
      }

      if (formData.buttons.length > 0) {
        components.push({
          type: "BUTTONS",
          buttons: formData.buttons.map((btn) => {
            const base = { type: btn.type, text: btn.text };
            if (btn.type === "URL") base.url = btn.url;
            if (btn.type === "PHONE_NUMBER")
              base.phone_number = btn.phone_number;
            if (btn.type === "COPY_CODE") base.coupon_code = btn.coupon_code;
            return base;
          }),
        });
      }

      const templateData = {
        name: formData.name,
        language: formData.language,
        status: "PENDING",
        category: formData.category,
        components,
        whatsappTemplateId: formData.whatsappTemplateId || `temp_${Date.now()}`,
        source: "custom",
      };

      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/templates`,
        templateData,
        {
          headers: {
            "Content-Type": "application/json",
            "x-email": localStorage.getItem("tenantEmail"),
          },
        }
      );
      alert("Template saved successfully!");
      // Reset form
      setFormData({
        name: "",
        language: "en",
        category: "UTILITY",
        header: { type: "NONE", text: "", example: "", url: "" },
        bodyText: "",
        footer: "",
        whatsappTemplateId: "",
        buttons: [],
      });
      setFile(null);
      setVariableSamples([]);
    } catch (err) {
      console.error(
        "Error saving template:",
        err.response?.data || err.message
      );
      setError(err.response?.data?.error || "Failed to save template");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="cx-template-page">
      <div className="cx-template-header">
        <div>
          <h1>Create WhatsApp Template</h1>
          <p>
            Build a reusable message template with headers, body variables, and
            optional buttons. Preview updates live as you type.
          </p>
        </div>
        <div className="cx-template-header-meta">
          <span className="cx-template-chip">
            Category: {formData.category}
          </span>
          <span className="cx-template-chip">
            Language: {formData.language}
          </span>
          <span className="cx-template-chip">
            Buttons: {formData.buttons.length}/10
          </span>
        </div>
      </div>

      <div className="cx-template-grid">
        {/* Form Section */}
        <div className="cx-template-card">
          <div className="cx-template-card-header">
            <div>
              <h2>Template Details</h2>
              <p>Fill out each section to create a valid template.</p>
            </div>
          </div>

          {error && (
            <div className="cx-template-alert" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="cx-template-form">
            <div className="cx-template-form-scroll">
              {/* Basic Information */}
              <div className="cx-template-section">
                <h5 className="cx-template-section-title">
                  <i className="bi bi-info-circle me-2"></i>
                  Basic Information
                </h5>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label required">
                      Template Name
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) =>
                        updateFormData("name", e.target.value)
                      }
                      placeholder="lowercase_only"
                      pattern="[a-z0-9_]+"
                      required
                    />
                    <div className="form-text">
                      Use lowercase letters, numbers, or underscores only
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label required">Category</label>
                    <select
                      className="form-control"
                      value={formData.category}
                      onChange={(e) =>
                        updateFormData("category", e.target.value)
                      }
                    >
                      <option value="UTILITY">Utility</option>
                      <option value="MARKETING">Marketing</option>
                      <option value="AUTHENTICATION">Authentication</option>
                    </select>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label required">Language</label>
                    <select
                      className="form-control"
                      value={formData.language}
                      onChange={(e) =>
                        updateFormData("language", e.target.value)
                      }
                    >
                      <option value="en">English</option>
                      <option value="es">Spanish</option>
                      <option value="fr">French</option>
                      <option value="en_US">English (US)</option>
                      <option value="pt_BR">Portuguese (Brazil)</option>
                      <option value="hi">Hindi</option>
                      <option value="ar">Arabic</option>
                    </select>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Template ID</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.whatsappTemplateId}
                      onChange={(e) =>
                        updateFormData("whatsappTemplateId", e.target.value)
                      }
                      placeholder="Will be auto-generated if empty"
                    />
                  </div>
                </div>
              </div>

              {/* Header Component */}
              <TemplateHeader
                header={formData.header}
                updateHeader={updateHeader}
                file={file}
                setFile={setFile}
              />

              {/* Body Component */}
              <TemplateBody
                bodyText={formData.bodyText}
                setBodyText={(text) => updateFormData("bodyText", text)}
                variableSamples={variableSamples}
                setVariableSamples={setVariableSamples}
              />

              {/* Footer Component */}
              <TemplateFooter
                footer={formData.footer}
                setFooter={(text) => updateFormData("footer", text)}
              />

              {/* Buttons Component */}
              <TemplateButtons
                buttons={formData.buttons}
                setButtons={(buttons) => updateFormData("buttons", buttons)}
                language={formData.language}
              />
            </div>

            {/* Submit Button */}
            <div className="cx-template-form-footer">
              <button
                type="submit"
                className="btn btn-success px-4"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    ></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-2"></i>
                    Save Template
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Preview Section */}
        <TemplatePreview
          formData={formData}
          variableSamples={variableSamples}
        />
      </div>
    </div>
  );
}

export default TemplateForm;
